import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Loader2, LogOut } from "lucide-react";
import {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AlphaBanner } from "@/components/custom/alpha-banner";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { ToolPickerGrid } from "@/components/custom/tool-picker-grid/tool-picker-grid";
import { useCurrentTools, useEntitlements, useSetTools } from "../api/hooks";
import { logout } from "../api/logout";
import { groupRefsByToolkit } from "../utils";
import { ConnectPanel } from "./connect-panel";

export const EnduserToolsPage = () => {
  const { data } = useEntitlements();
  const { data: current } = useCurrentTools();
  const setTools = useSetTools();

  // Seed the selection from what's already provisioned so a reload doesn't start
  // empty (which would let the next save overwrite the saved selection).
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(current.tools)
  );

  const groups = useMemo(() => groupRefsByToolkit(data.tools), [data.tools]);
  const totalTools = data.tools.length;

  // The gateway persists across reloads, so show the connect panel whenever one
  // exists — from the current save or the previously provisioned slug.
  const gatewaySlug = setTools.data?.gateway_slug || current.gateway_slug;

  const savedSet = useMemo(() => new Set(current.tools), [current.tools]);
  const isDirty = useMemo(
    () =>
      selected.size !== savedSet.size ||
      [...selected].some((ref) => !savedSet.has(ref)),
    [selected, savedSet]
  );

  const connectRef = useRef<HTMLDivElement>(null);
  // After a save completes the connect panel holds the MCP URL to copy, so bring
  // it into view at the top of the page.
  useEffect(() => {
    if (setTools.data) {
      connectRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [setTools.data]);

  const handleToggle = useCallback((ref: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(ref);
      } else {
        next.delete(ref);
      }
      return next;
    });
  }, []);

  const handleToggleMany = useCallback((refs: string[], checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const ref of refs) {
        if (checked) {
          next.add(ref);
        } else {
          next.delete(ref);
        }
      }
      return next;
    });
  }, []);

  const handleSave = useCallback(() => {
    setTools.mutate(Array.from(selected));
  }, [selected, setTools]);

  const handleLogout = useCallback(() => {
    logout();
  }, []);

  return (
    <ListLayout
      actions={
        <Button onClick={handleLogout} size="sm" variant="outline">
          <LogOut className="mr-2 size-4" />
          Log out
        </Button>
      }
      description="Choose the tools you want to use, then connect them to your MCP client."
      title="Your tools"
    >
      <AlphaBanner />

      {gatewaySlug ? (
        <div ref={connectRef}>
          <Suspense
            fallback={
              <div className="flex items-center justify-center rounded-xl border p-8">
                <Loader2 className="size-5 animate-spin text-muted-foreground" />
              </div>
            }
          >
            <ConnectPanel gatewaySlug={gatewaySlug} />
          </Suspense>
        </div>
      ) : null}

      <ToolPickerGrid
        emptyLabel="No tools have been made available to you yet."
        groups={groups}
        onToggle={handleToggle}
        onToggleMany={handleToggleMany}
        selected={selected}
      />

      {totalTools > 0 ? (
        <div className="sticky bottom-4 z-10 flex items-center justify-between gap-3 rounded-xl border border-primary/40 bg-card/95 px-4 py-3 shadow-lg ring-1 ring-primary/15 backdrop-blur">
          <span className="text-muted-foreground text-sm">
            {selected.size} of {totalTools} selected
          </span>
          <Button
            disabled={!isDirty || setTools.isPending}
            onClick={handleSave}
          >
            {setTools.isPending ? (
              <Loader2 className="mr-2 size-4 animate-spin" />
            ) : null}
            {selected.size > 0
              ? `Save & connect (${selected.size})`
              : "Save & connect"}
          </Button>
        </div>
      ) : null}
    </ListLayout>
  );
};
